/**
 * Computes the pixel position of a caret offset within a `<textarea>`,
 * relative to the textarea's own top-left padding-box corner (i.e. before
 * accounting for the textarea's own position on the page — callers combine
 * this with `getBoundingClientRect()` deltas to place a floating element).
 *
 * Standard "mirror div" technique: a hidden, off-screen `<div>` is styled to
 * exactly match the textarea's font/box metrics, filled with the text up to
 * the caret, and a marker `<span>` appended after it — the span's
 * `offsetTop`/`offsetLeft` then give the caret's pixel position, since the
 * div and textarea wrap text identically.
 */

export interface CaretCoordinates {
  top: number;
  left: number;
  height: number;
}

/** CSS properties that affect text layout/metrics and must be mirrored exactly for accurate measurement. */
const MIRRORED_PROPERTIES = [
  'boxSizing',
  'width',
  'overflowX',
  'overflowY',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'fontSizeAdjust',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'textDecoration',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
];

let mirrorDiv: HTMLDivElement | null = null;

function getMirrorDiv(): HTMLDivElement {
  if (mirrorDiv) return mirrorDiv;
  const div = document.createElement('div');
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.top = '0';
  div.style.left = '-9999px';
  div.style.whiteSpace = 'pre-wrap';
  div.style.wordWrap = 'break-word';
  document.body.appendChild(div);
  mirrorDiv = div;
  return div;
}

/** Returns the caret's pixel position (relative to the textarea's padding-box top-left, minus current scroll). */
export function getCaretCoordinates(el: HTMLTextAreaElement, position: number): CaretCoordinates {
  const div = getMirrorDiv();
  const computed = window.getComputedStyle(el);

  const style = div.style as unknown as Record<string, string>;
  const source = computed as unknown as Record<string, string>;
  for (const prop of MIRRORED_PROPERTIES) {
    style[prop] = source[prop];
  }

  div.textContent = el.value.substring(0, position);

  const span = document.createElement('span');
  // A trailing space would be collapsed by `pre-wrap` at a line break, so
  // fall back to a visible placeholder character to get a reliable offset.
  span.textContent = el.value.substring(position) || '.';
  div.appendChild(span);

  const coordinates: CaretCoordinates = {
    top: span.offsetTop - el.scrollTop,
    left: span.offsetLeft - el.scrollLeft,
    height: span.offsetHeight,
  };

  div.textContent = '';
  return coordinates;
}
